import { participationErrorKey } from '../../shared/utils/participation-error';
import { ScreenViewHistory } from "../../shared/screen-shell/screen-view-history";
import { navigateBackOrFallback } from "../../shared/screen-shell/screen-navigation.utils";
import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { GuestClaimInvitation, Player, PlayerType, RatingUpdate, SupportedLanguage } from "../../shared/models/player";
import { PlayerService } from "../../shared/services/player.service";
import { PlayerProfileStatsSummary } from "../../shared/models/stats";
import { StatsService } from "../../shared/services/stats.service";
import { ActivatedRoute, Router } from "@angular/router";
import { GuestParticipationRequest, PlayerEventInfo, PlayerEventInfoStatus, TeamMemberType } from "../../shared/models/playerEventInfo";
import { Event } from "../../shared/models/event";
import { ChatService } from "../../shared/services/chat.service";
import { PlayerEventInfoService } from "../../shared/services/playereventinfo.service";
import { Location } from "@angular/common";
import { BehaviorSubject, Observable, Subject, Subscription, of, firstValueFrom } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap } from "rxjs/operators";
import { LatLng } from "app/shared/models/latlng";
import { EventService } from "app/shared/services/event.service";
import { DelayedSpinnerService } from "../../shared/services/delayed-spinner.service";
import { AuthService } from "@auth0/auth0-angular";
import { LocaleService } from '../../shared/i18n/locale.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticatedSessionStateService } from "../../shared/services/authenticated-session-state.service";
import { nogoshIconClass } from "../../shared/icons/nogosh-icons.generated";
import { DialogService } from "../../core/dialog/dialog.service";
import { LoginIdentityMigrationService } from "../../shared/services/login-identity-migration.service";
import { FootballerAuthService, LoginMethods, SocialProvider, usesFootballerAuth } from "../../shared/services/footballer-auth.service";

interface ManagedDeleteResource {
  id: string;
  name: string;
}

interface ProfileDeleteBlockers {
  message?: string;
  managedEvents?: ManagedDeleteResource[];
  managedPlaySpaces?: ManagedDeleteResource[];
}

type ProfileSettingsMode = "menu" | "avatar" | "profile" | "contact" | "location" | "notifications" | "safety" | "account" | "language";

const PLACE_AUTOCOMPLETE_MIN_QUERY_LENGTH = 3;

@Component({
    selector: "player-detail",
    templateUrl: "./player-detail.component.html",
    styleUrls: ["../player.component.css"],
    standalone: false
})
export class PlayerDetailFormComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild("avatarLibraryInput") avatarLibraryInput: ElementRef<HTMLInputElement>;
  private avatarCameraVideo: ElementRef<HTMLVideoElement>;
  @ViewChild("avatarCameraVideo") set cameraVideo(element: ElementRef<HTMLVideoElement>) {
    this.avatarCameraVideo = element;
    if (element && this.avatarCameraStream) this.startAvatarVideo(element.nativeElement);
  }
  @ViewChild("avatarCameraCanvas") avatarCameraCanvas: ElementRef<HTMLCanvasElement>;
  private avatarCropCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild("avatarCropCanvas") set cropCanvas(element: ElementRef<HTMLCanvasElement>) {
    this.avatarCropCanvas = element;
    if (element) this.renderAvatarCrop();
  }

  @Input() public embedded: boolean = false;
  @Input() public embeddedPlayerEventInfo: PlayerEventInfo;
  @Output() public removedFromMatch = new EventEmitter<void>();
  @Input() public embeddedEvent: Event;

  public player: Player;
  public playerEventInfo: PlayerEventInfo;
  public profileSettingsMode: ProfileSettingsMode = "menu";
  public readonly footballerAuthEnabled = usesFootballerAuth;
  public loginMethods: LoginMethods | null = null;
  public loginMethodsBusy = false;
  public loginMethodsError = '';
  public showAvatarPreview = false;
  public profileShareBusy = false;
  public blockedPlayers: any[] = [];
  public blockedPlayersLoading = false;
  public profileSettingsScreenEntering = false;
  public profileSettingsScreenExiting = false;
  private profileSettingsTransitionTimer: number = null;
  private readonly profileSettingsTransitionMs = 150;

  public errorMsg: string;
  public loading = false;
  public readonly playerDetailReady$ = new BehaviorSubject<boolean>(false);

  public eventDetailMode: boolean = false;
  public playerEventInfoStatus: PlayerEventInfoStatus = new PlayerEventInfoStatus();
  public event: Event = new Event();

  public teamMemberType: TeamMemberType = new TeamMemberType();

  private currentPlayer: Player;

  public locationLatLng: LatLng;
  public selectedProfileCity: string;
  public selectedProfileAddress: string;
  private profileCityLatLng: LatLng;
  public profileCitySuggestions: string[] = [];
  public profileAddressSuggestions: string[] = [];
  public profileCityDropdownOpen = false;
  public profileAddressDropdownOpen = false;
  public profileCityLoading = false;
  public profileAddressLoading = false;
  private readonly profileCityQuery$ = new Subject<string>();
  private readonly profileAddressQuery$ = new Subject<string>();
  private readonly profileLocationSubscriptions = new Subscription();
  public chatPlayerMode: boolean = false;

  public showDeleteDialog: boolean = false;
  public showSetManagerConfirmDialog: boolean = false;
  public showRemoveFromMatchConfirmDialog: boolean = false;

  public ratingDefence: number;
  public ratingOffence: number;
  public ratingDefenceSliderValue: number;
  public ratingOffenceSliderValue: number;
  public ratingAverageDefence: number;
  public ratingAverageOffence: number;
  public profileStatsSummary: PlayerProfileStatsSummary;
  public ratingScale: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  public isReadonly: boolean = false; // Default rating value
  public playerType: PlayerType = new PlayerType();
  public selectedLanguage: SupportedLanguage = 'en';
  public claimInvitation: GuestClaimInvitation;
  public claimInvitationEmail: string = "";
  public claimInvitationMessage: string;
  public claimInvitationError: string;
  public claimInvitationLoading: boolean = false;
  public emailVerificationLoading: boolean = false;
  public emailVerificationCheckLoading: boolean = false;
  public emailVerificationMessage: string;
  public emailVerificationError: string;
  public authEmailVerified: boolean = null;
  public emailVerificationConfirmed = false;
  public deleteProfileError: string;
  public deleteProfileBlockers: ProfileDeleteBlockers;
  public deleteProfileLoading = false;
  public showLoginIdentityMigrationDialog = false;
  public loginIdentityMigrationLoading = false;
  public loginIdentityMigrationError = false;
  public showRemoveFromChatDialog = false;
  public removeFromChatInProgress = false;
  public chatSettingsSourceChatId: string;
  public chatSettingsCanManage = false;
  public removeFromMatchInProgress = false;
  public avatarUploading = false;
  public avatarCameraActive = false;
  public avatarCameraReady = false;
  private avatarCameraRequest = 0;
  public avatarCameraStarting = false;
  public avatarCropActive = false;
  public avatarCropZoom = 1;
  public avatarError: string;
  public avatarSuccess: string;
  private avatarCameraStream: MediaStream;
  private avatarCropImage: HTMLImageElement;
  private avatarCropOffsetX = 0;
  private avatarCropOffsetY = 0;
  private avatarCropDragging = false;
  private avatarCropDragLastX = 0;
  private avatarCropDragLastY = 0;
  private currentPlayerSubscription: Subscription;
  
  constructor(
    public auth: AuthService,
    private chatService: ChatService,
    public playerService: PlayerService,
    public route: ActivatedRoute,
    private playerEventinfoService: PlayerEventInfoService,
    private router: Router,
    public eventService: EventService,
    private _location: Location,
    private spinner: DelayedSpinnerService,
    private localeService: LocaleService,
    private translate: TranslateService,
    private zone: NgZone,
    private statsService: StatsService,
    private authenticatedSessionState: AuthenticatedSessionStateService,
    private dialogService: DialogService,
    private loginIdentityMigrationService: LoginIdentityMigrationService,
    private footballerAuth: FootballerAuthService
  ) {
    route.params.subscribe((val) => {
      this.currentPlayer = this.playerService.getCurrentPlayer();
      const eventDetailModeParam = this.route.snapshot.params["eventDetailMode"];
      this.eventDetailMode = eventDetailModeParam !== undefined ? JSON.parse(eventDetailModeParam) : false;
      this.player = JSON.parse(JSON.stringify(this.playerService.getCurrentPlayer()));

      let chatPlayerDetails = JSON.parse(localStorage.getItem("chatPlayer"));
      if (!this.eventDetailMode && ['avatar', 'profile', 'account'].includes(this.route.snapshot.queryParamMap.get('section'))) {
        chatPlayerDetails = null;
        this.chatPlayerMode = false;
      }

      if (chatPlayerDetails !== null) {
        this.chatPlayerMode = true;
        this.player = chatPlayerDetails;
      } else {
        if (this.eventDetailMode) {
          this.playerEventInfo = JSON.parse(localStorage.getItem("peiForPlayerDetail"));
          this.player = this.playerEventInfo.player;
          this.event = JSON.parse(localStorage.getItem("eventForPlayerDetail"));
          this.chatSettingsSourceChatId = localStorage.getItem("chatSettingsSourceChatId");
          this.chatSettingsCanManage = localStorage.getItem("chatSettingsCanManage") === "true";
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.embedded || (!changes.embeddedPlayerEventInfo && !changes.embeddedEvent)) {
      return;
    }

    this.applyEmbeddedContext();
    if (this.player && this.currentPlayer) {
      this.preloadRatings();
      this.loadProfileStatsSummary();
      this.playerDetailReady$.next(true);
    }
  }

  public typeaheadCitySelected(selectedCity: any) {
    this.player.city = selectedCity.value;
    this.selectedProfileCity = this.player.city;
    this.selectedProfileAddress = null;
    this.player.address = null;
    if (!this.hasProfileCity()) {
      this.clearProfileLocationFields();
      return;
    }
    this.playerService.getLatAndLng(this.player.city).subscribe((latLng) => {
      this.locationLatLng = latLng;
      this.profileCityLatLng = latLng;
      this.applyProfileLocationCoordinates(latLng);
    });
  }

  public typeaheadAddressSelected(selectedAddress: any) {
    this.player.address = selectedAddress.value;
    this.selectedProfileAddress = this.player.address;
    if (!this.hasSelectedProfileCity() || !this.hasProfileAddress()) {
      return;
    }
    this.playerService.getLatAndLng(this.player.city + selectedAddress).subscribe((latLng) => {
      this.locationLatLng = latLng;
      this.applyProfileLocationCoordinates(latLng);
    });
  }

  public onProfileCityInput(value: string): void {
    if (!this.player) {
      return;
    }

    this.player.city = value;
    this.selectedProfileCity = null;
    this.selectedProfileAddress = null;
    this.player.address = null;
    this.profileAddressSuggestions = [];
    this.profileAddressDropdownOpen = false;
    this.profileCityLatLng = null;
    this.player.location = null;
    this.player.addressLat = null;
    this.player.addressLong = null;
    this.locationLatLng = null;
    this.profileCityQuery$.next(value || "");
  }

  public onProfileAddressInput(value: string): void {
    if (!this.player) {
      return;
    }

    this.player.address = value;
    this.selectedProfileAddress = null;
    if (this.profileCityLatLng) {
      this.locationLatLng = this.profileCityLatLng;
      this.applyProfileLocationCoordinates(this.profileCityLatLng);
    }
    this.profileAddressQuery$.next(value || "");
  }

  public selectProfileCity(city: string): void {
    this.player.city = city;
    this.selectedProfileCity = city;
    this.selectedProfileAddress = null;
    this.player.address = null;
    this.profileCitySuggestions = [];
    this.profileAddressSuggestions = [];
    this.profileCityDropdownOpen = false;
    this.profileAddressDropdownOpen = false;
    this.playerService.getLatAndLng(city).subscribe((latLng) => {
      this.locationLatLng = latLng;
      this.profileCityLatLng = latLng;
      this.applyProfileLocationCoordinates(latLng);
    });
  }

  public selectProfileAddress(address: string): void {
    this.player.address = address;
    this.selectedProfileAddress = address;
    this.profileAddressSuggestions = [];
    this.profileAddressDropdownOpen = false;
    if (!this.hasSelectedProfileCity()) {
      return;
    }
    this.playerService.getLatAndLng(this.player.city + address).subscribe((latLng) => {
      this.locationLatLng = latLng;
      this.applyProfileLocationCoordinates(latLng);
    });
  }

  public showProfileCitySuggestions(): void {
    this.profileCityDropdownOpen = this.profileCitySuggestions.length > 0;
  }

  public showProfileAddressSuggestions(): void {
    this.profileAddressDropdownOpen = this.profileAddressSuggestions.length > 0;
  }

  public closeProfileLocationSuggestionsLater(): void {
    window.setTimeout(() => {
      this.profileCityDropdownOpen = false;
      this.profileAddressDropdownOpen = false;
    }, 150);
  }

  private getCitiesAsObservable(partialCityName: string) {
    if (!partialCityName || !partialCityName.trim()) {
      return of([]);
    }
    return this.playerService.getCities(partialCityName);
  }

  private getAddressAsObservable(partialAddres: string) {
    if (!this.hasSelectedProfileCity() || !partialAddres || !partialAddres.trim()) {
      return of([]);
    }
    if (!this.locationLatLng) {
      this.locationLatLng = new LatLng();
      if (this.player.location && this.player.location.coordinates) {
        this.locationLatLng.lat = this.player.location.coordinates[0];
        this.locationLatLng.lng = this.player.location.coordinates[1];
      }
    }
    if (!this.locationLatLng || !Number.isFinite(Number(this.locationLatLng.lat)) || !Number.isFinite(Number(this.locationLatLng.lng))) {
      return of([]);
    }
    return this.playerService.getAddress(partialAddres, this.locationLatLng.lat, this.locationLatLng.lng);
  }

  public profileCityTyped(value: string): void {
    if (!value || !value.trim()) {
      this.clearProfileLocationFields();
      return;
    }

    if (this.selectedProfileCity !== value) {
      this.selectedProfileCity = null;
      this.selectedProfileAddress = null;
      this.profileCityLatLng = null;
      this.player.location = null;
      this.player.addressLat = null;
      this.player.addressLong = null;
      this.locationLatLng = null;
    }
  }

  public profileAddressTyped(value: string): void {
    if (!value || !value.trim()) {
      this.selectedProfileAddress = null;
      if (this.profileCityLatLng) {
        this.locationLatLng = this.profileCityLatLng;
        this.applyProfileLocationCoordinates(this.profileCityLatLng);
      }
      return;
    }

    if (this.selectedProfileAddress !== value) {
      this.selectedProfileAddress = null;
      if (this.profileCityLatLng) {
        this.locationLatLng = this.profileCityLatLng;
        this.applyProfileLocationCoordinates(this.profileCityLatLng);
      }
    }
  }

  public activePlayingChanged(): void {
    if (!this.player || this.player.active) {
      return;
    }

    this.player.openProfile = false;
    this.player.allowTeammatesSOS = false;
    this.player.allowCitySOS = false;
  }

  public openProfileChanged(): void {
    if (!this.player || this.player.openProfile) {
      return;
    }

    this.player.allowCitySOS = false;
  }

  public openProfileSettingsMode(mode: Exclude<ProfileSettingsMode, "menu">): void {
    if (mode === 'account' && usesFootballerAuth) {
      if (this.loginMethodsBusy) return;
      this.loginMethodsBusy = true;
      const finish = this.spinner.acquire();
      void this.footballerAuth.loginMethods().then(methods => this.zone.run(() => {
        this.loginMethods = methods; this.loginMethodsError = ''; this.setProfileSettingsMode(mode);
      })).catch(() => this.zone.run(() => this.dialogService.alert({ title: this.translate.instant('footballerAuth.methodsTitle'),
        text: this.translate.instant('footballerAuth.methodsUnavailable') })))
        .finally(() => this.zone.run(() => { this.loginMethodsBusy = false; finish(); }));
      return;
    }
    this.profileSaved = false;
    this.player = JSON.parse(JSON.stringify(this.player));
    this.profileEditSnapshot = this.profileFormSnapshot();
    if (mode === "safety") {
      this.spinner.show();
      this.profileLocationSubscriptions.add(this.playerService.getBlockedPlayers().pipe(finalize(() => this.spinner.hide())).subscribe(
        players => { this.blockedPlayers = players || []; this.setProfileSettingsMode(mode); },
        () => this.dialogService.alert({title: this.translate.instant('common.alert'), text: this.translate.instant('safety.blockedUsersLoadFailed')})
      ));
      return;
    }
    this.setProfileSettingsMode(mode);
    this.errorMsg = null;
    this.avatarError = null;
    this.avatarSuccess = null;
    if (mode !== "avatar") {
      this.stopAvatarCamera();
      this.cancelAvatarCrop();
    }
    if (mode === "location") {
      this.syncSelectedProfileCityFromCurrentLocation();
    }

  }

  public unblockPlayer(playerId: string): void {
    if (this.blockedPlayersLoading) return;
    this.blockedPlayersLoading = true;
    this.playerService.unblockPlayer(playerId).subscribe(
      () => this.loadBlockedPlayers(),
      () => { this.blockedPlayersLoading = false; }
    );
  }

  private loadBlockedPlayers(): void {
    this.blockedPlayersLoading = true;
    this.playerService.getBlockedPlayers().subscribe(
      (players) => {
        this.blockedPlayers = players || [];
        this.blockedPlayersLoading = false;
      },
      () => { this.blockedPlayersLoading = false; }
    );
  }

  public canUseProfileShortcuts(): boolean {
    return this.profileSettingsMode === "menu" && this.currentPlayer && this.player && this.currentPlayer.id === this.player.id;
  }

  public openAvatar(): void {
    if (this.canUseProfileShortcuts()) this.openProfileShortcut('avatar');
    else if (this.player?.avatar) this.showAvatarPreview = true;
  }

  public openProfileShortcut(mode: "avatar" | "profile" | "location"): void {
    if (!this.canUseProfileShortcuts()) {
      return;
    }

    if (this.eventDetailMode || this.chatPlayerMode || this.embedded) {
      void this.router.navigate(['/player-detail', false], { queryParams: { section: mode } });
      return;
    }
    this.openProfileSettingsMode(mode);
  }

  public async linkProvider(provider: SocialProvider): Promise<void> {
    if (this.loginMethodsBusy) return;
    this.loginMethodsBusy = true; this.loginMethodsError = '';
    try { await this.footballerAuth.beginSocial(provider, '/player-detail/false?section=account', false, this.selectedLanguage, true); }
    catch (error) { this.zone.run(() => { this.loginMethodsError = this.methodError(error); }); }
    finally { this.zone.run(() => this.loginMethodsBusy = false); }
  }

  public confirmUnlinkProvider(provider: SocialProvider): void {
    if (this.loginMethodsBusy) return;
    this.dialogService.alert({ title: this.translate.instant('footballerAuth.unlinkTitle'),
      text: this.translate.instant('footballerAuth.unlinkConfirm'), closeLabel: this.translate.instant('common.cancel'),
      actionLabel: this.translate.instant('footballerAuth.unlinkAction'), destructive: true,
      action: () => { void this.unlinkProvider(provider); } });
  }

  private async unlinkProvider(provider: SocialProvider): Promise<void> {
    if (this.loginMethodsBusy) return;
    this.loginMethodsBusy = true; this.loginMethodsError = '';
    try { const methods = await this.footballerAuth.unlinkProvider(provider); this.zone.run(() => this.loginMethods = methods); }
    catch (error) { this.zone.run(() => this.loginMethodsError = this.methodError(error)); }
    finally { this.zone.run(() => this.loginMethodsBusy = false); }
  }

  private methodError(error: any): string {
    return error?.error?.code === 'reauthentication_required' ? 'footballerAuth.reauthenticate'
      : error?.error?.code === 'last_login_method' ? 'footballerAuth.lastMethod'
      : error?.status === 0 ? 'footballerAuth.connectionError' : 'footballerAuth.methodsUnavailable';
  }

  public setAccountPassword(): void {
    void this.router.navigate(['/auth'], { queryParams: { mode: 'request', target: '/player-detail/false?section=account' } });
  }

  private profileViewHistory: ScreenViewHistory<ProfileSettingsMode>;

  private setProfileSettingsMode(mode: ProfileSettingsMode): void {
    if (this.profileSettingsMode === mode && !this.profileSettingsScreenExiting) {
      return;
    }

    this.profileViewHistory?.push(mode);
    this.clearProfileSettingsTransitionTimer();
    this.profileSettingsScreenEntering = false;
    this.profileSettingsScreenExiting = true;
    this.profileSettingsTransitionTimer = window.setTimeout(() => {
      this.profileSettingsMode = mode;
      this.profileSettingsScreenExiting = false;
      this.startProfileSettingsEnterTransition();
      this.profileSettingsTransitionTimer = null;
    }, this.profileSettingsTransitionMs);
  }

  private startProfileSettingsEnterTransition(): void {
    this.profileSettingsScreenEntering = true;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.profileSettingsScreenEntering = false);
    });
  }

  private clearProfileSettingsTransitionTimer(): void {
    if (this.profileSettingsTransitionTimer !== null) {
      window.clearTimeout(this.profileSettingsTransitionTimer);
      this.profileSettingsTransitionTimer = null;
    }
  }

  private locationReturnRoute: string = null;
  private profileEditSnapshot: string;
  public profileSaved = false;

  private profileFormSnapshot(): string {
    const fields = ['name', 'nickName', 'email', 'phoneNumber', 'city', 'address', 'addressLat', 'addressLong', 'location', 'openProfile', 'allowTeammatesSOS', 'allowCitySOS'];
    const values = fields.map(key => this.player?.[key]);
    return JSON.stringify({values, language: this.selectedLanguage});
  }

  public hasProfileChanges(): boolean {
    return this.profileEditSnapshot !== this.profileFormSnapshot();
  }


  public closeProfileSettingsScreen(): void {
    if (this.loading) {
      return;
    }

    this.cancelPlayerDetailsChange();
    this.syncSelectedProfileCityFromCurrentLocation();
    this.selectedLanguage = this.localeService.currentLanguage;
    const returned = this.profileViewHistory?.back();
    if (!returned && !this.locationReturnRoute) this.setProfileSettingsMode("menu");
    this.errorMsg = null;
    this.avatarError = null;
    this.avatarSuccess = null;
    this.stopAvatarCamera();
    this.cancelAvatarCrop();
    if (!returned && this.locationReturnRoute) navigateBackOrFallback(this._location, this.router, [this.locationReturnRoute]);
  }

  public profileSettingsTitleKey(): string {
    switch (this.profileSettingsMode) {
      case "avatar":
        return "players.avatarTitle";
      case "profile":
        return "common.name";
      case "contact":
        return "common.contact";
      case "location":
        return "common.location";
      case "notifications":
        return "common.status";
      case "safety":
        return "safety.title";
      case "language":
        return "settings.language.label";
      case "account":
        return "players.profileMenuAccount";
      default:
        return "players.profile";
    }
  }

  public canMigrateSocialLoginToPassword(): boolean {
    if (usesFootballerAuth) return false;
    const authId = this.player && this.player.authId ? this.player.authId : "";
    return authId.startsWith("apple|") || authId.startsWith("google-oauth2|") || authId.startsWith("facebook|");
  }

  public openLoginIdentityMigrationDialog(): void {
    this.loginIdentityMigrationError = false;
    this.showLoginIdentityMigrationDialog = true;
  }

  public closeLoginIdentityMigrationDialog(): void {
    if (!this.loginIdentityMigrationLoading) {
      this.showLoginIdentityMigrationDialog = false;
    }
  }

  public beginLoginIdentityMigration(): void {
    if (this.loginIdentityMigrationLoading) {
      return;
    }
    this.loginIdentityMigrationLoading = true;
    this.loginIdentityMigrationError = false;
    this.loginIdentityMigrationService.beginPasswordMigration(this.router.url).subscribe(
      () => undefined,
      () => {
        this.loginIdentityMigrationLoading = false;
        this.loginIdentityMigrationError = true;
      }
    );
  }

  public changePlayerTeam(playerEventInfo: PlayerEventInfo, teamMemberType: string) {
    if (this.isHistoryEventDetail()) {
      return;
    }

    this.playerEventInfo.teamMemberType = teamMemberType;

    this.updateParticipation(playerEventInfo).subscribe(
      (updatedPlayerEventInfo) => {
        this.playerEventInfo = updatedPlayerEventInfo;
      },
      (error) => {
        console.log(error);
      }
    );
  }

  public mngUpdateInviteStatus(event: Event, status: string): void {
    const participant = this.playerEventInfo;
    const previousStatus = participant.status;
    this.playerEventInfo.status = status;

    this.updateParticipation(this.playerEventInfo).subscribe(
      (updatedPlayerEventInfo) => {
        if (this.embeddedPlayerEventInfo) {
  Object.assign(this.embeddedPlayerEventInfo, updatedPlayerEventInfo);
  this.playerEventInfo = this.embeddedPlayerEventInfo;
} else {
  this.playerEventInfo = updatedPlayerEventInfo;
}
      },
      (error) => {
        participant.status = previousStatus;
        this.dialogService.alert({
          title: this.translate.instant('common.alert'),
          text: this.translate.instant(participationErrorKey(error))
        });
      }
    );
  }

  public enterOrCreateNewChat(playerForChat: Player) {
    if (this.isGuestPlayer(playerForChat)) {
      return;
    }

    if (this.eventDetailMode && this.playerEventInfo && this.event) {
      localStorage.setItem("returnToPlayerDetailAfterChat", "true");
      localStorage.setItem("peiForPlayerDetail", JSON.stringify(this.playerEventInfo));
      localStorage.setItem("eventForPlayerDetail", JSON.stringify(this.event));
      localStorage.setItem("event", JSON.stringify(this.event));
    }

    this.spinner.show();
    this.loading = true;

    this.chatService.openDirectChat(playerForChat.id).subscribe(
      (thread) => {
        this.router.navigate(["/messages", { chatId: thread.chat.id }]);
      },
      (response) => {
        console.log(response.error);
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  public notifyPlayer(): void {
    if (this.isHistoryEventDetail()) {
      return;
    }

    if (this.isGuestPlayer(this.player)) {
      return;
    }

    this.eventService.notifyPlayer(this.playerEventInfo).subscribe(
      (updatedPc) => {
        this.playerEventInfo = updatedPc;
        this.spinner.hide();
        this.loading = false;
      },
      (error) => {
        console.log(error);
		if (error && error.status === 409) {
			this.playerEventInfo.reminderSentAt = new Date();
			this.showInvitationLimitReached();
		}
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  public invitePlayer(): void {
    if (this.isHistoryEventDetail()) {
      return;
    }

    if (this.isGuestPlayer(this.player)) {
      return;
    }

    this.eventService.invitePlayer(this.playerEventInfo).subscribe(
      (updatedPc) => {
        this.playerEventInfo = updatedPc;
        this.spinner.hide();
        this.loading = false;
      },
      (error) => {
        console.log(error);
		if (error && error.status === 409) {
			this.playerEventInfo.invitationSentAt = new Date();
			this.playerEventInfo.status = this.playerEventInfoStatus.NOT_ANSWERED;
			this.showInvitationLimitReached();
		}
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  private showInvitationLimitReached(): void {
	this.dialogService.alert({
		title: this.translate.instant('common.alert'),
		text: this.translate.instant('notifications.playerInvitationLimitReached')
	});
  }

  public requestSetAsManager(): void {
    if (this.isHistoryEventDetail() || this.isGuestPlayer(this.player)) {
      return;
    }

    this.showSetManagerConfirmDialog = true;
  }

  public confirmSetAsManager(): void {
    this.showSetManagerConfirmDialog = false;
    this.setAsManager();
  }

  public setAsManager() {
    if (this.isHistoryEventDetail()) {
      return;
    }

    if (this.isGuestPlayer(this.player)) {
      return;
    }

      this.eventService.changeManager(this.event.id, this.playerEventInfo.player.id).subscribe(
      (managerChanged) => {
        this.spinner.hide();
        this.loading = false;
        this.router.navigate(["/", false]);
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        this.loading = false;
      }
    );
    // this.event.manager = this.playerEventInfo.player;
    // this.event.locationId = this.event.playSpace.id;
    // this.event.playSpace.location = null;
    // this.eventService.updateEvent(this.event).subscribe(
    //   (updatedEvent) => {
    //     this.event = updatedEvent;
    //     localStorage.setItem("event", JSON.stringify(event));
    //     this.spinner.hide();
    //     this.loading = false;
    //   },
    //   (error) => {
    //     console.log(error);
    //     this.spinner.hide();
    //     this.loading = false;
    //   }
    // );
  }

  public requestRemovePlayerFromMatch(): void {
    if (this.isHistoryEventDetail() || this.removeFromMatchInProgress || !this.playerEventInfo) {
      return;
    }

    this.showRemoveFromMatchConfirmDialog = true;
  }

  public confirmRemovePlayerFromMatch(): void {
    this.showRemoveFromMatchConfirmDialog = false;
    this.removePlayerFromMatch();
  }

  public removePlayerFromMatch() {
    if (this.isHistoryEventDetail()) {
      return;
    }

    if (this.removeFromMatchInProgress || !this.playerEventInfo) {
      return;
    }

    this.removeFromMatchInProgress = true;
    if (this.isGuestPlayer(this.player)) {
      this.playerService.removeGuestFromEvent(this.player.id, this.event.id).subscribe(
        () => {
          this.finishRemovePlayerFromMatch();
        },
        (res) => {
          this.removeFromMatchInProgress = false;
          console.log(res.error);
        }
      );
      return;
    }

    const playerEventInfoId = this.playerEventInfo.id;
    this.playerEventinfoService.delete(playerEventInfoId).pipe(
      finalize(() => this.removeFromMatchInProgress = false)
    ).subscribe(
      () => {
        this.finishRemovePlayerFromMatch(playerEventInfoId);
      },
      (res) => {
        if (res && res.status === 404) {
          this.finishRemovePlayerFromMatch(playerEventInfoId);
          return;
        }
        console.log(res.error);
      }
    );
  }

  private finishRemovePlayerFromMatch(playerEventInfoId: string = null): void {
    const id = playerEventInfoId || (this.playerEventInfo ? this.playerEventInfo.id : null);
    if (this.event && this.event.playerEventInfos && id) {
      this.event.playerEventInfos = this.event.playerEventInfos.filter((item) => item.id !== id);
      localStorage.setItem("event", JSON.stringify(this.event));
    }

    this.removeFromMatchInProgress = false;
    if (this.embedded) {
      this.removedFromMatch.emit();
      return;
    }
    this.router.navigate(["/event-players", false]);
  }

  ngOnInit() {
    this.playerDetailReady$.next(false);
    this.errorMsg = null;
    this.applyEmbeddedContext();
    this.setupProfileLocationAutocomplete();
    this.profileLocationSubscriptions.add(this.auth.user$.subscribe((profile) => {
      this.authEmailVerified = profile && profile.email_verified !== undefined ? profile.email_verified : null;
    }));
    this.currentPlayerSubscription = this.playerService.currentPlayer$.subscribe((player) => {
      if (!player) {
        return;
      }
      this.currentPlayer = player;
      if (!this.eventDetailMode && !this.chatPlayerMode && this.profileSettingsMode === "menu") {
        this.player = JSON.parse(JSON.stringify(player));
        this.applyEmailVerificationState(player);
      }
    });
    const prepared = !this.embedded && this.route.snapshot.data['screen'];
    if (prepared?.player) this.player = JSON.parse(JSON.stringify(prepared.player));
    this.loginMethods = prepared?.loginMethods || null;
    this.preloadRatings();
    this.loadProfileStatsSummary();
    this.selectedLanguage = this.localeService.normalize(this.currentPlayer.preferredLanguage);
    this.loadGuestClaimInvitation();
    this.refreshEmailVerificationStatus();
    const section = this.route.snapshot.queryParamMap.get('section');
    if (!this.embedded && !this.eventDetailMode && !this.chatPlayerMode && this.isCurrentPlayerProfile()
      && ['avatar', 'profile', 'account'].includes(section) && (section !== 'account' || !usesFootballerAuth || this.loginMethods)) {
      this.profileSettingsMode = section as ProfileSettingsMode;
    }
    if (!this.embedded && !this.eventDetailMode && !this.chatPlayerMode && this.route.snapshot.queryParamMap.get('section') === 'location') {
      const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
      this.locationReturnRoute = returnTo === 'event-list' ? '/event-list' : returnTo === 'playspace' ? '/playspace/false' : returnTo === 'notifications' ? '/notifications' : null;
      this.profileSettingsMode = 'location';
      this.syncSelectedProfileCityFromCurrentLocation();
    }
    this.profileEditSnapshot = this.profileFormSnapshot();
    if (!this.embedded) {
      this.profileViewHistory = new ScreenViewHistory(this._location, this.router,
        `${this.currentPlayer.id}:profile:${this.player.id}`, () => this.profileSettingsMode, mode => {
          this.clearProfileSettingsTransitionTimer();
          this.stopAvatarCamera();
          this.cancelAvatarCrop();
          this.profileSettingsMode = mode;
          this.profileSettingsScreenEntering = false;
          this.profileSettingsScreenExiting = false;
        });
    }
    this.playerDetailReady$.next(true);
  }

  private setupProfileLocationAutocomplete(): void {
    this.profileLocationSubscriptions.add(this.profileCityQuery$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        const partialCityName = query ? query.trim() : "";
        if (partialCityName.length < PLACE_AUTOCOMPLETE_MIN_QUERY_LENGTH) {
          this.profileCityLoading = false;
          return of([]);
        }

        this.profileCityLoading = true;
        return this.getCitiesAsObservable(partialCityName).pipe(
          catchError(() => of([])),
          finalize(() => this.profileCityLoading = false)
        );
      })
    ).subscribe((cities) => {
      this.profileCitySuggestions = cities || [];
      this.profileCityDropdownOpen = this.profileSettingsMode === "location" && this.profileCitySuggestions.length > 0;
    }));

    this.profileLocationSubscriptions.add(this.profileAddressQuery$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        const partialAddress = query ? query.trim() : "";
        if (partialAddress.length < PLACE_AUTOCOMPLETE_MIN_QUERY_LENGTH || !this.hasSelectedProfileCity()) {
          this.profileAddressLoading = false;
          return of([]);
        }

        this.profileAddressLoading = true;
        return this.getAddressAsObservable(partialAddress).pipe(
          catchError(() => of([])),
          finalize(() => this.profileAddressLoading = false)
        );
      })
    ).subscribe((addresses) => {
      this.profileAddressSuggestions = addresses || [];
      this.profileAddressDropdownOpen = this.profileSettingsMode === "location" && this.profileAddressSuggestions.length > 0;
    }));
  }

  private applyEmbeddedContext(): void {
    if (!this.embedded) {
      return;
    }

    this.currentPlayer = this.playerService.getCurrentPlayer();
    this.eventDetailMode = true;
    this.chatPlayerMode = false;
    this.playerEventInfo = this.embeddedPlayerEventInfo;
    this.player = this.playerEventInfo ? this.playerEventInfo.player : null;
    this.event = this.embeddedEvent || new Event();

    if (this.event && this.event.manager && this.currentPlayer && this.currentPlayer.id === this.event.manager.id) {
      this.event.viewedByManager = true;
    }
  }

  private syncUpdatedPlayerRatings(updatedPlayer: Player): void {
    if (!updatedPlayer) {
      return;
    }

    if (!this.player || this.player.id !== updatedPlayer.id) {
      return;
    }

    this.player.ratingSummaryIncluded = false;
    this.player.ratingDefence = updatedPlayer.ratingDefence;
    this.player.ratingOffence = updatedPlayer.ratingOffence;

    if (this.playerEventInfo && this.playerEventInfo.player && this.playerEventInfo.player.id === updatedPlayer.id) {
      this.playerEventInfo.player.ratingDefence = updatedPlayer.ratingDefence;
      this.playerEventInfo.player.ratingOffence = updatedPlayer.ratingOffence;
      localStorage.setItem("peiForPlayerDetail", JSON.stringify(this.playerEventInfo));
    }

    if (this.event && this.event.playerEventInfos) {
      const playerEventInfo = this.event.playerEventInfos.find((item) =>
        item && item.player && item.player.id === updatedPlayer.id
      );

      if (playerEventInfo) {
        playerEventInfo.player.ratingDefence = updatedPlayer.ratingDefence;
        playerEventInfo.player.ratingOffence = updatedPlayer.ratingOffence;
        localStorage.setItem("eventForPlayerDetail", JSON.stringify(this.event));
      }
    }
  }

  @HostListener("window:focus")
  public onWindowFocus(): void {
    this.refreshEmailVerificationStatus();
  }

  @HostListener("document:visibilitychange")
  public onVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      this.refreshEmailVerificationStatus();
    }
  }

  public loadGuestClaimInvitation(): void {
    if (!this.canManageGuestClaimInvitation()) {
      return;
    }

    this.claimInvitationLoading = true;
    this.playerService.getGuestClaimInvitation(this.player.id).subscribe(
      (invitation) => {
        this.claimInvitation = invitation;
        this.claimInvitationEmail = invitation && invitation.email ? invitation.email : this.claimInvitationEmail;
        this.claimInvitationError = null;
        this.claimInvitationLoading = false;
      },
      (res) => {
        if (res.status !== 404) {
          this.claimInvitationError = this.getClaimInvitationError(res);
        }
        this.claimInvitationLoading = false;
      }
    );
  }

  public sendGuestClaimInvitation(): void {
    if (!this.canManageGuestClaimInvitation()) {
      return;
    }

    const email = this.claimInvitationEmail ? this.claimInvitationEmail.trim() : "";
    if (!email) {
      this.claimInvitationError = this.translate.instant('players.guestClaimEmailRequired');
      return;
    }

    this.claimInvitationLoading = true;
    this.playerService.createGuestClaimInvitation(this.player.id, { email }).subscribe(
      (invitation) => {
        this.claimInvitation = invitation;
        this.claimInvitationMessage = this.translate.instant('players.guestClaimInvitationSent');
        this.claimInvitationError = null;
        this.claimInvitationLoading = false;
      },
      (res) => {
        this.claimInvitationError = this.getClaimInvitationError(res);
        this.claimInvitationLoading = false;
      }
    );
  }

  public resendGuestClaimInvitation(): void {
    if (!this.canManageGuestClaimInvitation()) {
      return;
    }

    this.claimInvitationLoading = true;
    this.playerService.resendGuestClaimInvitation(this.player.id).subscribe(
      (invitation) => {
        this.claimInvitation = invitation;
        this.claimInvitationMessage = this.translate.instant('players.guestClaimInvitationSent');
        this.claimInvitationError = null;
        this.claimInvitationLoading = false;
      },
      (res) => {
        this.claimInvitationError = this.getClaimInvitationError(res);
        this.claimInvitationLoading = false;
      }
    );
  }

  public revokeGuestClaimInvitation(): void {
    if (!this.canManageGuestClaimInvitation()) {
      return;
    }

    this.claimInvitationLoading = true;
    this.playerService.revokeGuestClaimInvitation(this.player.id).subscribe(
      (invitation) => {
        this.claimInvitation = invitation;
        this.claimInvitationMessage = null;
        this.claimInvitationError = null;
        this.claimInvitationLoading = false;
      },
      (res) => {
        this.claimInvitationError = this.getClaimInvitationError(res);
        this.claimInvitationLoading = false;
      }
    );
  }

  public clearProfileLocation(): void {
    this.onProfileCityInput("");
    this.clearProfileLocationFields();
  }

  public openLearn(): void {
    this.router.navigate(["/help"], {queryParams: {section: "learn"}});
  }

  public onSubmit(): void {
    if (this.loading) return;
    this.normalizeProfileLocation();
    if (!this.isProfileLocationValidForSave()) {
      this.errorMsg = this.translate.instant("players.citySelectionRequired");
      return;
    }

    this.loading = true;
    this.spinner.show();

    this.profileSaved = false;
    this.playerService.updatePlayer(this.player, this.player.id).pipe(
      switchMap((profilePlayer) => this.playerService.updatePreferredLanguage(this.selectedLanguage).pipe(
        map((languagePlayer) => Object.assign(profilePlayer, languagePlayer, {
          preferredLanguage: this.selectedLanguage,
          languageSelectionCompleted: true
        }))
      ))
    ).subscribe(
      (player) => {
        this.localeService.use(this.selectedLanguage, player.id).subscribe(
          () => {
            this.player = player;
            this.currentPlayer = player;
            this.playerService.setCurrentPlayer(player);
            this.errorMsg = null;
            this.loading = false;
            this.spinner.hide();
            this.profileEditSnapshot = this.profileFormSnapshot();
            this.profileSaved = true;
          },
          () => {
            this.errorMsg = this.translate.instant('errors.translationLoad');
            this.loading = false;
            this.spinner.hide();
          }
        );
      },
      (response) => {
        // Keep the draft on a failed request so it can be retried without re-entering it.
        const errorBody = response?.error;
        const errorMessage = errorBody?.errorMsg || errorBody?.message || errorBody;
        const backendMessage = typeof errorMessage === 'string' ? errorMessage : '';
        this.errorMsg = backendMessage.indexOf("PROFILE_CITY_SELECTION_REQUIRED") >= 0
          ? this.translate.instant("players.citySelectionRequired")
          : response && response.status === 409
          ? this.translate.instant('players.nicknameTaken')
          : backendMessage
          ? backendMessage
          : this.translate.instant('errors.generic');

        console.log(response.error);
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  public shouldShowEmailVerificationReminder(): boolean {
    return !this.emailVerificationConfirmed && !this.eventDetailMode && !this.chatPlayerMode && !!this.player &&
      (this.isPlayerEmailUnverified(this.player) || this.authEmailVerified === false);
  }

  public isEmailVerified(): boolean {
    return !!this.player && !this.isPlayerEmailUnverified(this.player) &&
      (this.emailVerificationConfirmed || this.isPlayerEmailVerified(this.player) || this.authEmailVerified === true);
  }

  public sendVerificationEmail(): void {
    this.emailVerificationLoading = true;
    this.emailVerificationMessage = null;
    this.emailVerificationError = null;

    this.playerService.sendEmailVerification().subscribe(
      () => {
        this.emailVerificationMessage = this.translate.instant('notifications.emailVerificationSentAutoRefresh');
this.emailVerificationLoading = false;
      },
      (res) => {
        if (this.isAlreadyVerifiedResponse(res)) {
          this.refreshCurrentPlayerAfterEmailVerification();
          return;
        }

        this.emailVerificationError = this.translate.instant('notifications.emailVerificationSendFailed');
this.emailVerificationLoading = false;
      }
    );
  }

  public async openAvatarCamera(): Promise<void> {
    if (this.avatarUploading || this.avatarCameraStarting) {
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.avatarError = this.translate.instant("players.avatarCameraUnavailable");
      this.avatarSuccess = null;
      return;
    }

    this.stopAvatarCamera();
    const request = this.avatarCameraRequest;
    this.avatarCameraStarting = true;
    this.avatarError = null;
    this.avatarSuccess = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }, audio: false
      });
      if (request !== this.avatarCameraRequest) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      this.zone.run(() => {
        this.avatarCameraStream = stream;
        this.avatarCameraActive = true;
        // The conditional video view attaches the stream through its ViewChild setter.
      });
    } catch (e) {
      if (request !== this.avatarCameraRequest) return;
      this.zone.run(() => this.failAvatarCamera());
    }
  }

  private startAvatarVideo(video: HTMLVideoElement): void {
    const request = this.avatarCameraRequest;
    video.muted = true;
    video.srcObject = this.avatarCameraStream;
    video.play().catch(() => {
      if (request === this.avatarCameraRequest) this.zone.run(() => this.failAvatarCamera());
    });
  }

  public avatarVideoPlaying(): void {
    const video = this.avatarCameraVideo?.nativeElement;
    if (!this.avatarCameraActive || !video?.videoWidth || !video?.videoHeight) return;
    this.avatarCameraReady = true;
    this.avatarCameraStarting = false;
  }

  public failAvatarCamera(): void {
    this.stopAvatarCamera();
    this.avatarError = this.translate.instant("players.avatarCameraFailed");
    this.avatarSuccess = null;
  }

  public stopAvatarCamera(): void {
    this.avatarCameraRequest++;
    if (this.avatarCameraStream) {
      this.avatarCameraStream.getTracks().forEach((track) => track.stop());
      this.avatarCameraStream = null;
    }
    if (this.avatarCameraVideo) {
      this.avatarCameraVideo.nativeElement.srcObject = null;
    }
    this.avatarCameraActive = false;
    this.avatarCameraReady = false;
    this.avatarCameraStarting = false;
  }

  public captureAvatarPhoto(): void {
    if (this.avatarUploading || !this.avatarCameraReady || !this.avatarCameraVideo || !this.avatarCameraCanvas) {
      return;
    }
    const video = this.avatarCameraVideo.nativeElement;
    if (!video.videoWidth || !video.videoHeight) {
      this.avatarError = this.translate.instant("players.avatarCameraFailed");
      this.avatarSuccess = null;
      return;
    }

    const canvas = this.avatarCameraCanvas.nativeElement;
    const size = Math.min(video.videoWidth, video.videoHeight);
    const sourceX = Math.floor((video.videoWidth - size) / 2);
    const sourceY = Math.floor((video.videoHeight - size) / 2);
    canvas.width = 512;
    canvas.height = 512;

    const context = canvas.getContext("2d");
    if (!context) {
      this.avatarError = this.translate.instant("players.avatarCameraFailed");
      this.avatarSuccess = null;
      return;
    }
    context.drawImage(video, sourceX, sourceY, size, size, 0, 0, 512, 512);
    canvas.toBlob((blob) => this.zone.run(() => {
      if (!blob) {
  this.avatarError = this.translate.instant("players.avatarCameraFailed");
  this.avatarSuccess = null;
  return;
}
const sourceUrl = URL.createObjectURL(blob);
this.stopAvatarCamera();
this.loadAvatarCropSource(sourceUrl);
    }), "image/jpeg", 0.9);
  }

  public openAvatarLibrary(): void {
    if (this.avatarUploading || !this.avatarLibraryInput) {
      return;
    }
    this.stopAvatarCamera();
    this.avatarLibraryInput.nativeElement.click();
  }

  public avatarFileSelected(event: globalThis.Event): void {
    const input = event.target as HTMLInputElement;
    const file = input && input.files && input.files.length > 0 ? input.files[0] : null;
    if (input) {
      input.value = "";
    }
    if (!file) {
      return;
    }
    if (!file.type || file.type.indexOf("image/") !== 0) {
      this.avatarError = this.translate.instant("players.avatarInvalidType");
      this.avatarSuccess = null;
      return;
    }

    this.loadAvatarCropFile(file);
  }

  private loadAvatarCropFile(file: File): void {
    this.loadAvatarCropSource(URL.createObjectURL(file));
  }

  private loadAvatarCropSource(sourceUrl: string): void {
    const image = new Image();
    image.onload = () => this.zone.run(() => {
      URL.revokeObjectURL(sourceUrl);
      this.avatarCropImage = image;
this.avatarCropZoom = 1;
this.avatarCropOffsetX = 0;
this.avatarCropOffsetY = 0;
this.avatarCropActive = true;
this.avatarError = null;
this.avatarSuccess = null;
this.renderAvatarCrop();
    });
    image.onerror = () => this.zone.run(() => {
      URL.revokeObjectURL(sourceUrl);
      this.avatarError = this.translate.instant("players.avatarInvalidType");
this.avatarSuccess = null;
    });
    image.src = sourceUrl;
  }

  public avatarCropZoomChanged(value: string): void {
    this.avatarCropZoom = Number(value) || 1;
    this.renderAvatarCrop();
  }

  public avatarCropPointerDown(event: PointerEvent): void {
    if (!this.avatarCropActive || this.avatarUploading) {
      return;
    }
    this.avatarCropDragging = true;
    this.avatarCropDragLastX = event.clientX;
    this.avatarCropDragLastY = event.clientY;
    if (this.avatarCropCanvas) {
      this.avatarCropCanvas.nativeElement.setPointerCapture(event.pointerId);
    }
  }

  public avatarCropPointerMove(event: PointerEvent): void {
    if (!this.avatarCropDragging || this.avatarUploading) {
      return;
    }
    this.avatarCropOffsetX += event.clientX - this.avatarCropDragLastX;
    this.avatarCropOffsetY += event.clientY - this.avatarCropDragLastY;
    this.avatarCropDragLastX = event.clientX;
    this.avatarCropDragLastY = event.clientY;
    this.renderAvatarCrop();
  }

  public avatarCropPointerUp(event: PointerEvent): void {
    this.avatarCropDragging = false;
    if (this.avatarCropCanvas && this.avatarCropCanvas.nativeElement.hasPointerCapture(event.pointerId)) {
      this.avatarCropCanvas.nativeElement.releasePointerCapture(event.pointerId);
    }
  }

  public cancelAvatarCrop(): void {
    this.avatarCropActive = false;
    this.avatarCropImage = null;
  }

  public saveAvatarCrop(): void {
    if (this.avatarUploading || !this.avatarCropCanvas || !this.avatarCropImage) {
      return;
    }
    this.renderAvatarCrop();
    this.avatarCropCanvas.nativeElement.toBlob((blob) => this.zone.run(() => {
      if (!blob) {
  this.avatarError = this.translate.instant("players.avatarSaveFailed");
  this.avatarSuccess = null;
  return;
}
this.uploadAvatarFile(new File([blob], "profile-photo.jpg", { type: "image/jpeg" }));
    }), "image/jpeg", 0.9);
  }

  private renderAvatarCrop(): void {
    if (!this.avatarCropCanvas || !this.avatarCropImage) {
      return;
    }

    const canvas = this.avatarCropCanvas.nativeElement;
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    canvas.width = 512;
    canvas.height = 512;
    context.clearRect(0, 0, 512, 512);
    context.fillStyle = this.resolveThemeColor("--nogosh-white", "white");
    context.fillRect(0, 0, 512, 512);

    const image = this.avatarCropImage;
    const baseScale = Math.max(512 / image.naturalWidth, 512 / image.naturalHeight);
    const scale = baseScale * this.avatarCropZoom;
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    const centeredX = (512 - width) / 2;
    const centeredY = (512 - height) / 2;
    const minX = Math.min(0, 512 - width);
    const minY = Math.min(0, 512 - height);
    const x = Math.max(minX, Math.min(0, centeredX + this.avatarCropOffsetX));
    const y = Math.max(minY, Math.min(0, centeredY + this.avatarCropOffsetY));

    this.avatarCropOffsetX = x - centeredX;
    this.avatarCropOffsetY = y - centeredY;
    context.drawImage(image, x, y, width, height);
  }

  private resolveThemeColor(variableName: string, fallback: string): string {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    return value || fallback;
  }

  private uploadAvatarFile(file: File): void {
    this.avatarUploading = true;
this.avatarError = null;
this.avatarSuccess = null;
    this.playerService.updateAvatar(file).pipe(
      finalize(() => this.avatarUploading = false)
    ).subscribe(
      (player) => {
        if (this.profileSettingsMode === 'menu') this.player = JSON.parse(JSON.stringify(player));
        else if (this.player?.id === player.id) {
          this.player.avatar = player.avatar;
          this.player.avatarSource = player.avatarSource;
          this.player.emailVerified = player.emailVerified;
          this.player.email_verified = player.email_verified;
          this.player.version = player.version;
        }
this.currentPlayer = player;
this.stopAvatarCamera();
this.cancelAvatarCrop();
this.avatarSuccess = this.translate.instant("players.avatarSaved");
      },
      () => {
        this.avatarError = this.translate.instant("players.avatarSaveFailed");
      }
    );
  }

  public resetAvatar(): void {
    if (this.avatarUploading) {
      return;
    }
    this.avatarUploading = true;
this.avatarError = null;
this.avatarSuccess = null;
    this.playerService.resetAvatar().pipe(
      finalize(() => this.avatarUploading = false)
    ).subscribe(
      (player) => {
        if (this.profileSettingsMode === 'menu') this.player = JSON.parse(JSON.stringify(player));
        else if (this.player?.id === player.id) {
          this.player.avatar = player.avatar;
          this.player.avatarSource = player.avatarSource;
          this.player.emailVerified = player.emailVerified;
          this.player.email_verified = player.email_verified;
          this.player.version = player.version;
        }
this.currentPlayer = player;
this.cancelAvatarCrop();
this.avatarSuccess = this.translate.instant("players.avatarResetDone");
      },
      () => {
        this.avatarError = this.translate.instant("players.avatarResetFailed");
      }
    );
  }

  public checkEmailVerification(showPendingError: boolean = true): void {
    if (this.emailVerificationCheckLoading) {
      return;
    }

    this.emailVerificationCheckLoading = true;
    if (showPendingError) {
      this.emailVerificationError = null;
    }
    this.playerService.getCurrentUserProfile().subscribe(
      (player) => {
        if (this.profileSettingsMode === 'menu') this.player = JSON.parse(JSON.stringify(player));
        else if (this.player?.id === player.id) {
          this.player.emailVerified = player.emailVerified;
          this.player.email_verified = player.email_verified;
          this.player.version = player.version;
        }
this.currentPlayer = player;
this.playerService.setCurrentPlayer(player);
this.applyEmailVerificationState(player);
if (this.isPlayerEmailUnverified(player) || this.authEmailVerified === false) {
  this.emailVerificationMessage = null;
  if (showPendingError) {
    this.emailVerificationError = this.translate.instant('notifications.emailVerificationPending');
  }
} else if (this.isEmailVerified()) {
  this.emailVerificationMessage = this.translate.instant('notifications.emailVerificationConfirmed');
  this.emailVerificationError = null;
}
this.emailVerificationCheckLoading = false;
      },
      () => {
        if (showPendingError) {
  this.emailVerificationError = this.translate.instant('notifications.emailVerificationCheckFailed');
}
this.emailVerificationCheckLoading = false;
      }
    );
  }

  private refreshEmailVerificationStatus(): void {
    if (this.isEmailVerificationRedirect() || this.shouldShowEmailVerificationReminder()) {
      this.checkEmailVerification(false);
    }
  }

  private isEmailVerificationRedirect(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.get("success") === "true" && params.get("code") === "success";
  }

  public cancelPlayerDetailsChange(): void {
    this.player = JSON.parse(localStorage.getItem("player"));
    this.playerService.setCurrentPlayer(this.player);
  }

  private normalizeProfileLocation(): void {
    this.player.city = this.trimToNull(this.player.city);
    if (!this.player.city) {
      this.clearProfileLocationFields();
      return;
    }

    this.player.address = this.trimToNull(this.player.address);
  }

  public isProfileLocationValidForSave(): boolean {
    if (this.profileSettingsMode !== "location" || !this.hasProfileCity()) {
      return true;
    }

    return this.isProfileCityValidForSave() && this.isProfileAddressValidForSave();
  }

  public isProfileCityValidForSave(): boolean {
    if (this.profileSettingsMode !== "location" || !this.hasProfileCity()) {
      return true;
    }

    return this.hasSelectedProfileCity() && this.hasProfileLocationCoordinates();
  }

  public isProfileAddressValidForSave(): boolean {
    if (!this.hasProfileAddress()) {
      return true;
    }

    return this.hasSelectedProfileAddress();
  }

  public hasSelectedProfileCity(): boolean {
    return !!(this.hasProfileCity() && this.selectedProfileCity === this.player.city);
  }

  public hasSelectedProfileAddress(): boolean {
    return !!(this.hasProfileAddress() && this.selectedProfileAddress === this.player.address);
  }

  private hasProfileLocationCoordinates(): boolean {
    return !!(this.player && this.player.location && this.player.location.coordinates
      && this.player.location.coordinates.length >= 2
      && Number.isFinite(Number(this.player.location.coordinates[0]))
      && Number.isFinite(Number(this.player.location.coordinates[1])));
  }

  private applyProfileLocationCoordinates(latLng: LatLng): void {
    if (!latLng || !Number.isFinite(Number(latLng.lat)) || !Number.isFinite(Number(latLng.lng))) {
      this.player.location = null;
      this.player.addressLat = null;
      this.player.addressLong = null;
      return;
    }

    this.player.addressLat = latLng.lat;
    this.player.addressLong = latLng.lng;
    this.player.location = {
      type: "Point",
      coordinates: [latLng.lat, latLng.lng]
    } as GeoJSON.Point;
  }

  private syncSelectedProfileCityFromCurrentLocation(): void {
    this.selectedProfileCity = this.hasProfileCity() && this.hasProfileLocationCoordinates() ? this.player.city : null;
    this.selectedProfileAddress = this.hasProfileAddress() && this.hasProfileLocationCoordinates() ? this.player.address : null;
    if (this.hasProfileLocationCoordinates()) {
      this.locationLatLng = new LatLng();
      this.locationLatLng.lat = Number(this.player.location.coordinates[0]);
      this.locationLatLng.lng = Number(this.player.location.coordinates[1]);
      if (!this.hasProfileAddress()) {
        this.profileCityLatLng = this.locationLatLng;
      }
    } else {
      this.locationLatLng = null;
      this.profileCityLatLng = null;
    }
  }

  private clearProfileLocationFields(): void {
    this.player.city = null;
    this.player.address = null;
    this.player.location = null;
    this.player.addressLat = null;
    this.player.addressLong = null;
    this.locationLatLng = null;
    this.selectedProfileCity = null;
    this.selectedProfileAddress = null;
    this.profileCityLatLng = null;
  }

  public hasProfileCity(): boolean {
    return !!(this.player && this.player.city && this.player.city.trim());
  }

  public hasProfileAddress(): boolean {
    return !!(this.player && this.player.address && this.player.address.trim());
  }

  private trimToNull(value: string): string {
    const trimmed = value ? value.trim() : "";
    return trimmed ? trimmed : null;
  }

  public openDeleteProfileDialog(): void {
    this.deleteProfileError = null;
this.deleteProfileBlockers = null;
this.showDeleteDialog = true;
  }

  public closeDeleteProfileDialog(): void {
    this.deleteProfileError = null;
this.deleteProfileBlockers = null;
this.deleteProfileLoading = false;
this.showDeleteDialog = false;
  }

  public deleteProfile(force: boolean = false): void {
    this.deleteProfileError = null;
if (force) {
  this.deleteProfileBlockers = null;
}
this.deleteProfileLoading = true;

    this.playerService.deletePlayerById(this.currentPlayer.id, force).subscribe(
      () => {
        this.authenticatedSessionState.clear();
this.playerService.clearCurrentPlayer();
	this.deleteProfileLoading = false;
	this.showDeleteDialog = false;
	this.auth.logout({
	  logoutParams: {
	    returnTo: window.location.origin
	  }
	} as any);
      },
      (res) => {
        console.log(res);
        const blockers = this.getDeleteProfileBlockers(res);
if (blockers && this.hasDeleteProfileBlockers(blockers)) {
  this.deleteProfileBlockers = blockers;
  this.deleteProfileError = null;
} else {
  this.deleteProfileBlockers = null;
  this.deleteProfileError = this.getDeleteProfileError(res);
}
this.deleteProfileLoading = false;
      }
    );
  }

  public hasDeleteProfileBlockers(blockers: ProfileDeleteBlockers = this.deleteProfileBlockers): boolean {
    return !!blockers && (
      (blockers.managedEvents && blockers.managedEvents.length > 0) ||
      (blockers.managedPlaySpaces && blockers.managedPlaySpaces.length > 0)
    );
  }

  private getDeleteProfileBlockers(res: any): ProfileDeleteBlockers {
    if (!res || res.status !== 409 || !res.error || typeof res.error !== "object") {
      return null;
    }
    const blockers = res.error as ProfileDeleteBlockers;
    return this.hasDeleteProfileBlockers(blockers) ? blockers : null;
  }

  private getDeleteProfileError(res: any): string {
    if (res && res.status === 401) {
      return this.translate.instant('players.deleteProfileErrors.sessionExpired');
    }
    if (res && res.status === 403) {
      return this.translate.instant('players.deleteProfileErrors.forbidden');
    }
    if (res && res.status === 409) {
      return this.translate.instant('players.deleteProfileErrors.hasManagedResources');
    }
    if (res && res.error) {
      if (typeof res.error === "string") {
        return this.humanizeDeleteProfileError(res.error);
      }
      if (res.error.message) {
        return res.error.message;
      }
      if (res.error.error) {
        return res.error.error;
      }
    }
    return this.translate.instant('players.deleteProfileErrors.generic');
  }

  private humanizeDeleteProfileError(message: string): string {
    if (!message) {
      return this.translate.instant('players.deleteProfileErrors.generic');
    }
    if (message.indexOf("can't be deleted") > -1 && message.indexOf("match/playspace") > -1) {
      return this.translate.instant('players.deleteProfileErrors.hasManagedResources');
    }
    return message;
  }

  public goBack(): void {
    if (this.embedded) {
      return;
    }

    navigateBackOrFallback(this._location, this.router, ["/event-list"]);
  }

  public playerDetailHeaderBack(): void {
    if (this.isProfileSettingsSubscreen()) {
      this.closeProfileSettingsScreen();
      return;
    }

    this.goBack();
  }

  public shouldShowPlayerDetailHeader(): boolean {
    return !this.embedded && (this.eventDetailMode || this.isProfileSettingsSubscreen());
  }

  public playerDetailHeaderTitleKey(): string {
    return this.isProfileSettingsSubscreen() ? this.profileSettingsTitleKey() : "common.details";
  }

  public isProfileSettingsSubscreen(): boolean {
    return !this.eventDetailMode && !this.chatPlayerMode && this.profileSettingsMode !== "menu";
  }

  public canRemovePlayerFromChat(): boolean {
    return !!(this.chatSettingsSourceChatId
      && this.chatSettingsCanManage
      && this.player
      && this.player.id
      && this.currentPlayer
      && this.currentPlayer.id
      && this.player.id !== this.currentPlayer.id
      && (this.isChatSettingsPlayerDetail() || !this.isMatchManagerPlayer()));
  }

  public isChatSettingsPlayerDetail(): boolean {
    return !!(this.chatSettingsSourceChatId && !this.chatPlayerMode);
  }

  public openRemoveFromChatDialog(): void {
    if (!this.canRemovePlayerFromChat() || this.removeFromChatInProgress) {
      return;
    }
    this.showRemoveFromChatDialog = true;
  }

  public removePlayerFromChat(): void {
    if (!this.canRemovePlayerFromChat() || this.removeFromChatInProgress) {
      return;
    }
    this.removeFromChatInProgress = true;
    this.chatService.removeChatMember(this.chatSettingsSourceChatId, this.player.id).subscribe(() => {
      const chatId = this.chatSettingsSourceChatId;
      this.clearChatSettingsPlayerDetailContext();
      this.removeFromChatInProgress = false;
      this.showRemoveFromChatDialog = false;
      this.router.navigate(["/chat-settings", {chatId}]);
    }, error => {
      console.log(error);
      this.removeFromChatInProgress = false;
      this.showRemoveFromChatDialog = false;
    });
  }

  private clearChatSettingsPlayerDetailContext(): void {
    localStorage.removeItem("chatSettingsSourceChatId");
    localStorage.removeItem("chatSettingsCanManage");
    localStorage.removeItem("peiForPlayerDetail");
    localStorage.removeItem("eventForPlayerDetail");
  }

  public displayAttendanceStatus(status: string): string {
    if (status === this.playerEventInfoStatus.CONFIRMED) {
      return this.translate.instant('events.confirmed');
    }
    if (status === this.playerEventInfoStatus.NOT_ANSWERED) {
      return this.translate.instant('events.notAnswered');
    }
    if (status === this.playerEventInfoStatus.DECLINED) {
      return this.translate.instant('stats.declined');
    }
    if (status === this.playerEventInfoStatus.NOT_NOTIFIED) {
      return this.translate.instant('events.notNotified');
    }
    return status || "";
  }

  public isMatchManagerPlayer(): boolean {
    return !!(this.eventDetailMode && !this.isChatSettingsPlayerDetail() && this.event && this.event.manager && this.player && this.event.manager.id === this.player.id);
  }

  public isMatchFirstTeamPlayer(): boolean {
    return !!(this.playerEventInfo && this.playerEventInfo.teamMemberType === this.teamMemberType.FIRST_TEAM);
  }

  public isMatchReservePlayer(): boolean {
    return !!(this.playerEventInfo && this.playerEventInfo.teamMemberType === this.teamMemberType.RESERVE);
  }

  public matchPlayerRoleLabel(): string {
    if (this.isMatchManagerPlayer()) {
      return this.translate.instant('common.manager');
    }
    if (this.isGuestPlayer(this.player)) {
      return this.accountlessPlayerLabel(this.player);
    }
    if (this.isMatchFirstTeamPlayer()) {
      return this.translate.instant('notifications.firstTeam');
    }
    if (this.isMatchReservePlayer()) {
      return this.translate.instant('players.reserve');
    }
    return this.translate.instant('common.details');
  }

  public loginMethodLabel(): string {
    const provider = this.loginProvider();
    if (provider === "google-oauth2") {
      return this.translate.instant('players.loginMethodGoogle');
    }
    if (provider === "facebook") {
      return this.translate.instant('players.loginMethodFacebook');
    }
    if (provider === "auth0") {
      return this.translate.instant('players.loginMethodPassword');
    }
    return this.translate.instant('players.loginMethodOther');
  }

  public loginMethodIconClass(): string {
    const provider = this.loginProvider();
    if (provider === "google-oauth2") {
      return nogoshIconClass("auth.google");
    }
    if (provider === "facebook") {
      return nogoshIconClass("auth.facebook");
    }
    if (provider === "auth0") {
      return nogoshIconClass("auth.password");
    }
    return nogoshIconClass("auth.login");
  }

  public profileStatValue(value: number): number | string {
    if (!this.profileStatsSummary) {
      return "–";
    }
    return value != null ? value : 0;
  }

  public teamGoalScore(): string {
    if (!this.profileStatsSummary) {
      return "–";
    }
    const value = this.profileStatsSummary && this.profileStatsSummary.teamGoalDifference != null
      ? this.profileStatsSummary.teamGoalDifference
      : 0;
    return value > 0 ? "+" + value : String(value);
  }

  public joinedAt(): Date {
    if (this.profileStatsSummary && this.profileStatsSummary.joinedAt) {
      return new Date(this.profileStatsSummary.joinedAt);
    }
    if (!this.player || !this.player.id || !/^[0-9a-fA-F]{24}$/.test(this.player.id)) {
      return null;
    }
    return new Date(parseInt(this.player.id.substring(0, 8), 16) * 1000);
  }

  public displayCity(): string {
    if (!this.player || !this.player.city) {
      return "";
    }
    return this.player.city.split(",")[0].trim();
  }

  public async shareProfile(): Promise<void> {
    if (!this.isCurrentPlayerProfile() || this.profileShareBusy) return;
    this.profileShareBusy = true;
    let shareUrl: string;
    try {
      const share = await firstValueFrom(this.playerService.createProfileShare());
      if (share.playerId !== this.player.id || !/^[A-Za-z0-9_-]{43}$/.test(share.token)) throw new Error('invalid_share');
      shareUrl = window.location.origin + '/profile/' + encodeURIComponent(share.playerId) + '#share=' + share.token;
    } catch (_) {
      this.dialogService.alert({ title: this.translate.instant('players.shareProfile'), text: this.translate.instant('errors.generic') });
      return;
    } finally { this.profileShareBusy = false; }
    const shareData = {
      title: this.player && this.player.nickName ? this.player.nickName : this.translate.instant("players.shareProfileFallbackTitle"),
      text: shareUrl,
      url: shareUrl
    };

    const navigatorWithShare = navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
      clipboard?: Clipboard;
    };

    if (navigatorWithShare.share) {
      navigatorWithShare.share(shareData).catch(error => {
        if (error?.name !== 'AbortError') this.offerProfileCopy(shareUrl);
      });
      return;
    }

    if (navigatorWithShare.clipboard && navigatorWithShare.clipboard.writeText) {
      navigatorWithShare.clipboard.writeText(shareUrl).catch(() => this.offerProfileCopy(shareUrl));
      return;
    }

    this.copyTextFallback(shareUrl);
  }

  private offerProfileCopy(url: string): void {
    this.dialogService.alert({ title: this.translate.instant('players.shareProfile'), text: url,
      actionLabel: this.translate.instant('common.copy'), action: () => {
        if (navigator.clipboard?.writeText) void navigator.clipboard.writeText(url).catch(() => this.copyTextFallback(url));
        else this.copyTextFallback(url);
      } });
  }

  private publicProfileUrl(): string {
    if (!this.player || !this.player.id) {
      return window.location.href;
    }
    return window.location.origin + "/profile/" + this.player.id;
  }

  private loginProvider(): string {
    if (!this.player || !this.player.authId || this.player.authId.indexOf("|") < 0) {
      return "";
    }
    return this.player.authId.split("|")[0];
  }

  private copyTextFallback(value: string): void {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "true");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }

  private loadProfileStatsSummary(): void {
    if (!this.player || !this.player.id || this.isGuestPlayer(this.player)) {
      this.profileStatsSummary = null;
      return;
    }

    if (this.player.profileStatsSummary) {
      this.profileStatsSummary = this.player.profileStatsSummary;
      this.statsService.cachePlayerProfileSummary(this.player.profileStatsSummary);
      return;
    }

    this.statsService.getPlayerProfileSummary(this.player.id).subscribe(
      (summary) => {
        this.profileStatsSummary = summary;
        if (summary) {
          this.player.profileStatsSummary = summary;
          this.statsService.cachePlayerProfileSummary(summary);
        }
      },
      () => this.profileStatsSummary = null
    );
  }

  public canRatePlayer(): boolean {
    return this.player?.canRate === true && !this.isCurrentPlayerProfile() && !this.isHistoryEventDetail() && !this.isChatSettingsPlayerDetail();
  }

  public setDefence(rating: number): void {
    if (!this.canRatePlayer()) {
      return;
    }

    this.ratingDefence = rating;
    this.ratingDefenceSliderValue = rating;
    this.updateDefenceRating(rating);
  }

  public setDefenceFromSlider(rating: number): void {
    const roundedRating = this.normalizedSliderRating(rating);

    if (roundedRating <= 0) {
      this.clearDefence();
      return;
    }

    this.setDefence(roundedRating);
  }

  public ratingSliderPercent(rating: number): number {
    if (!rating || rating <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, rating * 10));
  }

  public displaySliderRating(rating: number): number {
    const roundedRating = this.normalizedSliderRating(rating);
    return roundedRating > 0 ? roundedRating : null;
  }

  private normalizedSliderRating(rating: number): number {
    return Math.max(0, Math.min(10, Math.round(Number(rating) || 0)));
  }

  public clearDefence(): void {
    if (!this.canRatePlayer()) {
      return;
    }

    this.ratingDefence = null;
    this.ratingDefenceSliderValue = 0;
    this.updateDefenceRating(0);
  }

  private updateDefenceRating(rating: number): void {
    this.errorMsg = null;
    const ratingDefenceUpdate: RatingUpdate = {
      ratedBy: this.currentPlayer.id,
      rating,
    };
  
    this.playerService.updatePlayerDefenceRating(this.player.id, ratingDefenceUpdate).subscribe(
      (response) => {
        this.syncUpdatedPlayerRatings(response);
        this.preloadRatings();
      },
      (error) => {
        this.preloadRatings();
        if (error?.status === 403) this.player.canRate = false;
        this.errorMsg = this.translate.instant('errors.generic');
      }
    );
  }

  public setOffence(rating: number): void {
    if (!this.canRatePlayer()) {
      return;
    }

    this.ratingOffence = rating;
    this.ratingOffenceSliderValue = rating;
    this.updateOffenceRating(rating);
  }

  public setOffenceFromSlider(rating: number): void {
    const roundedRating = this.normalizedSliderRating(rating);

    if (roundedRating <= 0) {
      this.clearOffence();
      return;
    }

    this.setOffence(roundedRating);
  }

  public clearOffence(): void {
    if (!this.canRatePlayer()) {
      return;
    }

    this.ratingOffence = null;
    this.ratingOffenceSliderValue = 0;
    this.updateOffenceRating(0);
  }

  public isHistoryEventDetail(): boolean {
    return !!this.event && this.event.historyEvent === true && this.event.active === false;
  }

  public isCurrentPlayerProfile(): boolean {
    return !!(this.currentPlayer && this.currentPlayer.id && this.player && this.player.id && this.currentPlayer.id === this.player.id);
  }

  public canShowMatchHistoryAction(): boolean {
    return !!(this.currentPlayer
      && this.player
      && this.player.id
      && !this.isHistoryEventDetail()
      && !this.isGuestPlayer(this.player));
  }

  public playerDetailShellClasses(): {[klass: string]: boolean} {
    return {
      "player-profile-page": true,
      "player-profile-page-main": !this.shouldShowPlayerDetailHeader(),
      "profile-embedded-container": this.embedded,
      "player-profile-page-match": this.eventDetailMode,
      "player-profile-page-settings": !this.eventDetailMode && !this.chatPlayerMode,
      "player-profile-page-readonly": this.isHistoryEventDetail()
    };
  }

  public canShowFootballerFriendsAction(): boolean {
    return !!(this.currentPlayer
      && this.player
      && this.player.id
      && this.isCurrentPlayerProfile()
      && !this.eventDetailMode
      && !this.chatPlayerMode
      && !this.isHistoryEventDetail()
      && !this.isGuestPlayer(this.player));
  }

  public canShowFootballReferencesAction(): boolean {
    return !!(this.currentPlayer
      && this.player
      && this.player.id
      && !this.isCurrentPlayerProfile()
      && !this.isHistoryEventDetail()
      && !this.isGuestPlayer(this.player));
  }

  public openFootballerFriends(): void {
    if (!this.canShowFootballerFriendsAction()) {
      return;
    }
    this.router.navigate(["/player", "footballer-friends"], {
      queryParams: this.playerReviewQueryParams()
    });
  }

  public openFootballReferences(): void {
    if (!this.canShowFootballReferencesAction()) {
      return;
    }
    this.router.navigate(["/player", this.player.id, "football-references"], {
      queryParams: this.playerReviewQueryParams()
    });
  }

  public openMatchHistory(): void {
    if (!this.canShowMatchHistoryAction()) {
      return;
    }
    this.router.navigate(["/player", this.player.id, "match-history"], {
      queryParams: this.playerReviewQueryParams()
    });
  }

  private playerReviewQueryParams(): {[key: string]: string} {
    const queryParams: {[key: string]: string} = {};
    if (this.embeddedEvent && this.embeddedEvent.id && this.embeddedPlayerEventInfo && this.player && this.player.id) {
      queryParams.returnToPlayerDetail = "true";
      queryParams.returnPlayerId = this.player.id;
      queryParams.returnEventId = this.embeddedEvent.id;
    }
    if (this.embeddedEvent && this.embeddedEvent.id && this.embeddedPlayerEventInfo && this.currentPlayer
      && this.embeddedEvent.manager && this.embeddedEvent.manager.id === this.currentPlayer.id
      && this.embeddedPlayerEventInfo.teamMemberType === this.teamMemberType.CANDIDATE) {
      queryParams.contextEventId = this.embeddedEvent.id;
    }
    return queryParams;
  }

  private updateOffenceRating(rating: number): void {
    this.errorMsg = null;
    const ratingOffenceUpdate: RatingUpdate = {
      ratedBy: this.currentPlayer.id,
      rating,
    };
  
    this.playerService.updatePlayerOffenceRating(this.player.id, ratingOffenceUpdate).subscribe(
      (response) => {
        this.syncUpdatedPlayerRatings(response);
        this.preloadRatings();
      },
      (error) => {
        this.preloadRatings();
        if (error?.status === 403) this.player.canRate = false;
        this.errorMsg = this.translate.instant('errors.generic');
      }
    );
  }

  ngOnDestroy(): void {
    this.profileViewHistory?.destroy();
    this.clearProfileSettingsTransitionTimer();
    if (this.currentPlayerSubscription) {
      this.currentPlayerSubscription.unsubscribe();
    }
    this.profileLocationSubscriptions.unsubscribe();
    this.stopAvatarCamera();
    this.cancelAvatarCrop();
    localStorage.setItem("chatPlayer", null);
    localStorage.removeItem("chatSettingsSourceChatId");
    localStorage.removeItem("chatSettingsCanManage");
  }

  preloadRatings(): void {
    if (!this.currentPlayer || !this.currentPlayer.id || !this.player) {
      return;
    }

    if (this.player.ratingSummaryIncluded) {
      this.ratingDefence = this.player.viewerRatingDefence || null;
      this.ratingOffence = this.player.viewerRatingOffence || null;
      this.ratingDefenceSliderValue = this.ratingDefence || 0;
      this.ratingOffenceSliderValue = this.ratingOffence || 0;
      this.ratingAverageDefence = this.player.ratingAverageDefence || null;
      this.ratingAverageOffence = this.player.ratingAverageOffence || null;
      return;
    }
    const currentPlayerId = this.currentPlayer.id;
    
    if (!this.player.ratingDefence) {
      this.player.ratingDefence = [];
    }
    if (!this.player.ratingOffence) {
      this.player.ratingOffence = [];
    }

    const defenceRating = this.player.ratingDefence.find(rating => rating.ratedBy === currentPlayerId);
    if (defenceRating && defenceRating.rating > 0) {
      this.ratingDefence = defenceRating.rating;
    } else {
      this.ratingDefence = null;
    }
    this.ratingDefenceSliderValue = this.ratingDefence || 0;
    
    const offenceRating = this.player.ratingOffence.find(rating => rating.ratedBy === currentPlayerId);
    if (offenceRating && offenceRating.rating > 0) {
      this.ratingOffence = offenceRating.rating;
    } else {
      this.ratingOffence = null;
    }
    this.ratingOffenceSliderValue = this.ratingOffence || 0;

    const activeDefenceRatings = this.player.ratingDefence.filter(rating => rating.rating > 0);
    const activeOffenceRatings = this.player.ratingOffence.filter(rating => rating.rating > 0);
    this.ratingAverageDefence = activeDefenceRatings.length > 0 ? Math.round(activeDefenceRatings.reduce((acc, rating) => acc + rating.rating, 0) / activeDefenceRatings.length) : null;
    this.ratingAverageOffence = activeOffenceRatings.length > 0 ? Math.round(activeOffenceRatings.reduce((acc, rating) => acc + rating.rating, 0) / activeOffenceRatings.length) : null;
  }

  public isGuestPlayer(player: Player): boolean {
    return !!player && (
      player.guest === true ||
      player.playerType === this.playerType.MANAGED_PLAYER ||
      player.playerType === this.playerType.GUEST
    );
  }

  public isManagedPlayer(player: Player): boolean {
    return !!player && (
      player.managedPlayer === true ||
      player.playerType === this.playerType.MANAGED_PLAYER
    );
  }

  public accountlessPlayerLabel(player: Player): string {
    return this.translate.instant(this.isManagedPlayer(player) ? 'guests.managedPlayer' : 'guests.guest');
  }

  public canManageGuestClaimInvitation(): boolean {
    return !this.isChatSettingsPlayerDetail() && !this.isHistoryEventDetail() && this.eventDetailMode && this.event && this.event.viewedByManager && !this.chatPlayerMode && this.isManagedPlayer(this.player);
  }

  private getClaimInvitationError(res: any): string {
    return res && res.error && (res.error.errorMsg || res.error.message)
      ? res.error.errorMsg || res.error.message
      : this.translate.instant('players.guestClaimUpdateFailed');
  }

  private refreshCurrentPlayerAfterEmailVerification(): void {
    this.playerService.getCurrentUserProfile().subscribe(
      (player) => {
        if (this.profileSettingsMode === 'menu') this.player = JSON.parse(JSON.stringify(player));
        else if (this.player?.id === player.id) {
          this.player.emailVerified = player.emailVerified;
          this.player.email_verified = player.email_verified;
          this.player.version = player.version;
        }
this.currentPlayer = player;
this.playerService.setCurrentPlayer(player);
this.applyEmailVerificationState(player);
if (this.isEmailVerified()) {
  this.emailVerificationMessage = this.translate.instant('notifications.emailVerificationAlreadyConfirmed');
  this.emailVerificationError = null;
}
this.emailVerificationLoading = false;
      },
      () => {
        this.emailVerificationMessage = this.translate.instant('notifications.emailVerificationAlreadyConfirmedReload');
this.emailVerificationError = null;
this.emailVerificationLoading = false;
      }
    );
  }

  private applyEmailVerificationState(player: Player): void {
    if (this.isPlayerEmailVerified(player)) {
      this.authEmailVerified = true;
      this.emailVerificationConfirmed = true;
      this.emailVerificationError = null;
      return;
    }

    if (this.isPlayerEmailUnverified(player)) {
      this.authEmailVerified = false;
      this.emailVerificationConfirmed = false;
      if (this.emailVerificationConfirmed) {
        this.emailVerificationMessage = null;
      }
    }
  }

  private isPlayerEmailVerified(player: Player): boolean {
    return !!player && (player.emailVerified === true || player.email_verified === true);
  }

  private isPlayerEmailUnverified(player: Player): boolean {
    return !!player && (player.emailVerified === false || player.email_verified === false);
  }

  private isAlreadyVerifiedResponse(res: any): boolean {
    const backendMessage = this.getBackendMessage(res);
    return backendMessage.indexOf("ALREADY_VERIFIED") > -1 || backendMessage.indexOf("EMAIL_ALREADY_VERIFIED") > -1 || backendMessage.indexOf("already verified") > -1;
  }

  private getBackendMessage(res: any): string {
    if (!res || !res.error) {
      return "";
    }

    return (res.error.errorMsg || res.error.message || res.error.error || res.error || "").toString();
  }

  private updateParticipation(playerEventInfo: PlayerEventInfo): Observable<PlayerEventInfo> {
    if (this.isGuestPlayer(playerEventInfo.player)) {
      const request: GuestParticipationRequest = {
        status: playerEventInfo.status,
        jersey: playerEventInfo.jersey,
        teamMemberType: playerEventInfo.teamMemberType,
      };
      return this.playerService.updateGuestParticipation(playerEventInfo.player.id, this.event.id, request);
    }

    return this.playerEventinfoService.update(playerEventInfo, this.currentPlayer);
  }
}
